import clsx from 'clsx';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import type { MemberDTO } from '@wd/shared';
import { api, ApiError, errorText, post } from '../../lib/api';
import { useLayout } from '../../lib/media';
import { queryClient } from '../../lib/queries';
import { Icon } from '../../ui/icons';
import { Avatar, Kbds, LogoMark, Modal } from '../../ui/primitives';
import { Showcase } from './Showcase';
import './login.css';

// 登录（设计稿 25a–25h）。规则：个人微信首次扫码需用管理员登记的工作邮箱绑定成员（25g），之后扫码直接登录；
// 工作区内成员才能登录；扫码 2 分钟过期；验证码 10 分钟有效、每 60 秒可重发一次；登录后回到原来要去的页面。

interface Ticket {
  id: string;
  secret: string;
  qrUrl: string | null;
  expiresAt: string;
  simulated: boolean;
}
interface Card {
  name: string;
  initial: string;
  teamName: string | null;
}
interface AuthConfig {
  wechat: boolean;
  wechatSimulated: boolean;
  email: boolean;
  dev: boolean;
  /** ICP 备案号（国内网站必须在页面底部展示并链接到工信部网站） */
  icp?: string | null;
}

type Step =
  | { s: 'home' }
  | { s: 'qr'; ticket: Ticket; expired?: boolean }
  | { s: 'scanned'; ticket: Ticket; member: Card }
  | { s: 'bind'; ticket: Ticket | null }
  | { s: 'code'; email: string }
  | { s: 'not-member'; email: string }
  | { s: 'join-sent'; email: string; adminName: string };

const inWeChat = () => /MicroMessenger/i.test(navigator.userAgent);

export function LoginPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = safeNext(params.get('next'));
  const narrow = useLayout() === 'mobile';
  const [config, setConfig] = useState<AuthConfig | null>(null);
  const [step, setStep] = useState<Step>(params.get('bind') === '1' ? { s: 'bind', ticket: null } : { s: 'home' });
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  /** 测试环境：验证码直接显示在页面上（生产环境发到邮箱） */
  const [devCode, setDevCode] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState<'data' | 'privacy' | 'admin' | null>(null);

  useEffect(() => {
    api<AuthConfig>('/auth/config')
      .then(setConfig)
      .catch(() => setConfig({ wechat: false, wechatSimulated: false, email: true, dev: false }));
  }, []);

  const done = useCallback(async () => {
    // 从 Claude 连接器的授权页过来的：授权页是服务端页面，要整页跳回去
    if (next.startsWith('/oauth/')) {
      location.replace(next);
      return;
    }
    await queryClient.invalidateQueries();
    navigate(next, { replace: true });
  }, [navigate, next]);

  const go = useCallback((s: Step) => {
    setError(null);
    setStep(s);
  }, []);

  // Esc：其他登录方式（回到首页）
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && step.s !== 'home' && !info) go({ s: 'home' });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [step.s, info, go]);

  const startQr = async () => {
    setError(null);
    if (inWeChat() && config && !config.wechatSimulated) {
      location.href = `/api/auth/wx/oauth?next=${encodeURIComponent(next)}`;
      return;
    }
    try {
      setBusy(true);
      go({ s: 'qr', ticket: await post<Ticket>('/auth/wx/qr') });
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const sendEmail = async (address: string, onNotMember: () => void): Promise<boolean> => {
    setError(null);
    try {
      const r = await post<{ devCode?: string }>('/auth/email/send', { email: address });
      setDevCode(r.devCode ?? null);
      return true;
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) onNotMember();
      else setError(errorText(e));
      return false;
    }
  };

  const emailLogin = async () => {
    const address = email.trim().toLowerCase();
    if (!address) return setError('先输入工作邮箱');
    setBusy(true);
    const ok = await sendEmail(address, () => go({ s: 'not-member', email: address }));
    setBusy(false);
    if (ok) go({ s: 'code', email: address });
  };

  const wechatOn = config?.wechat !== false;
  let body: ReactNode;
  switch (step.s) {
    case 'home':
      body = (
        <>
          <Heading
            narrow={narrow}
            title={narrow ? '登录 Wattsonic 日报' : '登录'}
            desc={narrow ? undefined : wechatOn ? '用微信扫码，或用管理员登记的工作邮箱。' : '用管理员登记的工作邮箱登录。'}
            logo={narrow}
          />
          <div className="lg-actions" style={narrow ? { marginTop: 36 } : undefined}>
            {wechatOn && (
              <button type="button" className="lg-btn primary" onClick={() => void startQr()} disabled={busy}>
                <Icon name="team" color="currentColor" />
                {inWeChat() ? '微信一键登录' : '微信扫码登录'}
              </button>
            )}
            {wechatOn && <div className="lg-or">或</div>}
            <form
              className="col"
              style={{ gap: 10 }}
              onSubmit={(e) => {
                e.preventDefault();
                void emailLogin();
              }}
            >
              <label className="lg-field">
                <span className="sr-only">工作邮箱</span>
                <input type="email" inputMode="email" autoComplete="email" placeholder="工作邮箱" value={email} onChange={(e) => setEmail(e.target.value)} />
              </label>
              {/* 没开微信登录时，邮箱是唯一入口，用主按钮 */}
              <button type="submit" className={clsx('lg-btn', wechatOn ? 'ghost' : 'primary')} disabled={busy}>
                用邮箱登录
              </button>
            </form>
          </div>
          <ErrorLine error={error} />
          <p className={clsx('lg-note', narrow && 'lg-center')} style={narrow ? { marginTop: 28 } : undefined}>
            只有被管理员加入工作区的成员可以登录。
            {wechatOn && (
              <>
                {narrow && <br />}首次扫码需要用工作邮箱验证一次{narrow ? '身份' : ''}。
              </>
            )}
          </p>
        </>
      );
      break;
    case 'qr':
      body = <QrStep ticket={step.ticket} expired={!!step.expired} narrow={narrow} dev={!!config?.dev} go={go} onConfirmed={done} setError={setError} error={error} />;
      break;
    case 'scanned':
      body = <ScannedStep ticket={step.ticket} member={step.member} narrow={narrow} dev={!!config?.dev} go={go} onConfirmed={done} setError={setError} error={error} />;
      break;
    case 'bind':
      body = <BindStep ticket={step.ticket} narrow={narrow} initialEmail={email} go={go} onDone={done} sendEmail={sendEmail} setError={setError} error={error} />;
      break;
    case 'code':
      body = <CodeStep email={step.email} narrow={narrow} go={go} onDone={done} sendEmail={sendEmail} setError={setError} error={error} />;
      break;
    case 'not-member':
      body = (
        <>
          <Heading narrow={narrow} logo={narrow} title="你还不在这个工作区" desc={`${step.email} 没有被加入 Wattsonic 的日报工作区。`} />
          <div className="lg-actions">
            <button
              type="button"
              className="lg-btn primary"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  const r = await post<{ ok: true; adminName: string }>('/auth/join-request', { email: step.email });
                  go({ s: 'join-sent', email: step.email, adminName: r.adminName });
                } catch (e) {
                  setError(errorText(e));
                } finally {
                  setBusy(false);
                }
              }}
            >
              请管理员邀请我
            </button>
            <button type="button" className="lg-btn ghost" onClick={() => go({ s: 'home' })}>
              换个账号登录
            </button>
          </div>
          <ErrorLine error={error} />
          <p className={clsx('lg-note row', narrow && 'lg-center')} style={{ gap: 8, justifyContent: narrow ? 'center' : undefined }}>
            <Icon name="info" size={12} />
            <span>发送后，管理员会在收件箱看到你的申请</span>
          </p>
        </>
      );
      break;
    case 'join-sent':
      body = (
        <>
          <Heading narrow={narrow} logo={narrow} title="申请已发送" desc={`${step.adminName}会在收件箱看到你的申请。通过后，用 ${step.email} 登录即可。`} />
          <div className="lg-actions">
            <button type="button" className="lg-btn ghost" onClick={() => go({ s: 'home' })}>
              返回登录
            </button>
          </div>
        </>
      );
      break;
  }

  const foot = (
    <div className="lg-foot">
      <button type="button" onClick={() => setInfo('data')}>
        数据说明
      </button>
      <button type="button" onClick={() => setInfo('privacy')}>
        隐私
      </button>
      <button type="button" onClick={() => setInfo('admin')}>
        {narrow ? '需要帮助？联系管理员' : '联系管理员'}
      </button>
      {config?.dev && (
        <a className="lg-icp" href="/__test">
          测试面板
        </a>
      )}
      {config?.icp && (
        <a className="lg-icp" href="https://beian.miit.gov.cn/" target="_blank" rel="noreferrer">
          {config.icp}
        </a>
      )}
    </div>
  );

  return (
    <div className={clsx('lg-page', narrow && 'narrow')}>
      <div className="lg-left">
        <div className="lg-brand">
          <LogoMark size={20} />
          <img src="/wattsonic-wordmark.svg" alt="Wattsonic" className="wordmark only-dark" />
          <img src="/wattsonic-wordmark-dark.svg" alt="Wattsonic" className="wordmark only-light" />
        </div>
        <main className="lg-body" key={step.s}>
          {body}
          {devCode && (step.s === 'code' || step.s === 'bind') && (
            <p className={clsx('lg-note', narrow && 'lg-center')}>
              测试环境：验证码是 <b>{devCode}</b>（正式环境会发到邮箱）
            </p>
          )}
        </main>
        {foot}
      </div>
      {!narrow && <Showcase />}
      {info && <InfoModal kind={info} onClose={() => setInfo(null)} />}
    </div>
  );
}

function safeNext(n: string | null) {
  return n && n.startsWith('/') && !n.startsWith('//') && !n.startsWith('/login') ? n : '/';
}

function Heading({ title, desc, narrow, logo }: { title: string; desc?: ReactNode; narrow: boolean; logo?: boolean }) {
  return (
    <div className={clsx(narrow && 'lg-center')}>
      {logo && (
        <div className="row" style={{ justifyContent: 'center', marginBottom: 28 }}>
          <LogoMark size={40} />
        </div>
      )}
      <h1 className="lg-title">{title}</h1>
      {desc && <p className="lg-desc">{desc}</p>}
    </div>
  );
}

function ErrorLine({ error }: { error: string | null }) {
  return error ? (
    <p className="lg-error" role="alert">
      {error}
    </p>
  ) : null;
}

function useCountdown(seconds: number, startKey: unknown) {
  const [left, setLeft] = useState(seconds);
  useEffect(() => {
    setLeft(seconds);
    const t = setInterval(() => setLeft((x) => (x > 0 ? x - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [seconds, startKey]);
  return left;
}

type StepProps = {
  narrow: boolean;
  go: (s: Step) => void;
  setError: (e: string | null) => void;
  error: string | null;
};

// ---------------------------------------------------------------------------
// 25b 扫码
// ---------------------------------------------------------------------------

function useTicketPoll(ticket: Ticket, go: (s: Step) => void, onConfirmed: () => void, setError: (e: string | null) => void, enabled: boolean) {
  const stopped = useRef(false);
  useEffect(() => {
    stopped.current = false;
    if (!enabled) return;
    const tick = async () => {
      if (stopped.current) return;
      try {
        const r = await api<{ status: string; member: Card | null }>(`/auth/wx/qr/${ticket.id}?secret=${encodeURIComponent(ticket.secret)}`);
        if (stopped.current) return;
        if (r.status === 'scanned' && r.member) go({ s: 'scanned', ticket, member: r.member });
        else if (r.status === 'needs_bind') go({ s: 'bind', ticket });
        else if (r.status === 'expired') go({ s: 'qr', ticket, expired: true });
        else if (r.status === 'cancelled') go({ s: 'qr', ticket: await post<Ticket>('/auth/wx/qr') });
        else if (r.status === 'confirmed') {
          stopped.current = true;
          await post(`/auth/wx/qr/${ticket.id}/login`, { secret: ticket.secret });
          onConfirmed();
        }
      } catch (e) {
        setError(errorText(e));
      }
    };
    const t = setInterval(tick, 1500);
    return () => {
      stopped.current = true;
      clearInterval(t);
    };
  }, [ticket, go, onConfirmed, setError, enabled]);
}

function DevScan({ ticket, setError }: { ticket: Ticket; setError: (e: string | null) => void }) {
  const [users, setUsers] = useState<MemberDTO[]>([]);
  useEffect(() => {
    api<MemberDTO[]>('/auth/dev-users').then(setUsers).catch(() => setUsers([]));
  }, []);
  const scan = (memberId: string | null) => post('/auth/wx/dev-scan', { id: ticket.id, memberId }).catch((e) => setError(errorText(e)));
  return (
    <div className="lg-dev">
      <span>开发环境 · 模拟扫码：</span>
      {users.slice(0, 4).map((u) => (
        <button key={u.id} type="button" onClick={() => void scan(u.id)}>
          {u.name}
        </button>
      ))}
      <button type="button" onClick={() => void scan(null)}>
        未绑定的微信
      </button>
    </div>
  );
}

function QrStep({ ticket, expired, narrow, dev, go, onConfirmed, setError, error }: StepProps & { ticket: Ticket; expired: boolean; dev: boolean; onConfirmed: () => void }) {
  useTicketPoll(ticket, go, onConfirmed, setError, !expired);
  const refresh = async () => {
    try {
      go({ s: 'qr', ticket: await post<Ticket>('/auth/wx/qr') });
    } catch (e) {
      setError(errorText(e));
    }
  };
  const align = narrow ? undefined : { margin: '32px 0 0' };
  return (
    <>
      <Heading narrow={narrow} logo={narrow} title="用微信扫一扫" desc="打开手机微信 › 右上角「+」› 扫一扫" />
      <div className="lg-qr-card" style={align}>
        <div className={clsx('lg-qr', !ticket.qrUrl && 'placeholder')}>
          {ticket.qrUrl ? <img src={ticket.qrUrl} alt="微信登录二维码" /> : '微信登录二维码'}
          {expired && (
            <div className="veil">
              <span>二维码已过期</span>
              <button type="button" className="lg-link" onClick={() => void refresh()}>
                <Icon name="refresh" size={14} />
                刷新
              </button>
            </div>
          )}
        </div>
      </div>
      <div className="lg-row" style={narrow ? undefined : { justifyContent: 'flex-start' }}>
        <Icon name="clock" size={14} />
        <span>{expired ? '二维码已过期' : '二维码 2 分钟内有效'}</span>
        <span className="c4">·</span>
        <button type="button" className="c2" onClick={() => void refresh()}>
          刷新
        </button>
      </div>
      {dev && ticket.simulated && !expired && <DevScan ticket={ticket} setError={setError} />}
      <ErrorLine error={error} />
      <div className="lg-row" style={{ marginTop: 32, justifyContent: narrow ? 'center' : 'flex-start' }}>
        <button type="button" className="lg-link" onClick={() => go({ s: 'home' })}>
          <Icon name="chevL" size={14} />
          <span>其他登录方式</span>
          {!narrow && <Kbds keys={['Esc']} />}
        </button>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// 25c 已扫码，等待手机确认
// ---------------------------------------------------------------------------

function ScannedStep({ ticket, member, narrow, dev, go, onConfirmed, setError, error }: StepProps & { ticket: Ticket; member: Card; dev: boolean; onConfirmed: () => void }) {
  useTicketPoll(ticket, go, onConfirmed, setError, true);
  const rescan = async () => {
    try {
      await post(`/auth/wx/qr/${ticket.id}/cancel`, { secret: ticket.secret });
      go({ s: 'qr', ticket: await post<Ticket>('/auth/wx/qr') });
    } catch (e) {
      setError(errorText(e));
    }
  };
  return (
    <>
      <Heading narrow={narrow} logo={narrow} title="扫码成功" desc="请在手机微信上点「允许」" />
      <div className="lg-person">
        <Avatar initial={member.initial} seed={member.name} size={32} />
        <div className="col flex1" style={{ gap: 2 }}>
          <span className="c1" style={{ fontSize: 14 }}>
            {member.name}
          </span>
          <span className="t-meta">{[member.teamName, 'Wattsonic'].filter(Boolean).join(' · ')}</span>
        </div>
        <span className="lg-spin" aria-label="等待确认" />
      </div>
      {dev && ticket.simulated && (
        <div className="lg-dev">
          <span>开发环境：</span>
          <button type="button" onClick={() => void post('/auth/wx/dev-confirm', { id: ticket.id }).catch((e) => setError(errorText(e)))}>
            模拟手机上点「允许」
          </button>
        </div>
      )}
      <ErrorLine error={error} />
      <p className="lg-row" style={narrow ? undefined : { justifyContent: 'flex-start' }}>
        <span className="c4">不是你？</span>
        <button type="button" className="c3" onClick={() => void rescan()}>
          重新扫码
        </button>
      </p>
    </>
  );
}

// ---------------------------------------------------------------------------
// 25g 第一次用微信登录：用工作邮箱验证码绑定
// ---------------------------------------------------------------------------

function BindStep({
  ticket,
  narrow,
  initialEmail,
  go,
  onDone,
  sendEmail,
  setError,
  error,
}: StepProps & {
  ticket: Ticket | null;
  initialEmail: string;
  onDone: () => void;
  sendEmail: (email: string, onNotMember: () => void) => Promise<boolean>;
}) {
  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState('');
  const [sentKey, setSentKey] = useState(0);
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const left = useCountdown(60, sentKey);
  const codeRef = useRef<HTMLInputElement>(null);

  const send = async () => {
    if (!email.trim()) return setError('先输入工作邮箱');
    const ok = await sendEmail(email.trim(), () => go({ s: 'not-member', email: email.trim() }));
    if (ok) {
      setSent(true);
      setSentKey((k) => k + 1);
      codeRef.current?.focus();
    }
  };
  const bind = async () => {
    if (code.trim().length < 6) return setError('输入邮件里的 6 位验证码');
    setBusy(true);
    try {
      if (ticket) await post(`/auth/wx/qr/${ticket.id}/bind`, { secret: ticket.secret, email: email.trim(), code: code.trim() });
      else await post('/auth/wx/bind-openid', { email: email.trim(), code: code.trim() });
      onDone();
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) go({ s: 'not-member', email: email.trim() });
      else setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className={clsx(narrow && 'lg-center')}>
        <div className="lg-pair" style={{ justifyContent: narrow ? 'center' : 'flex-start', marginBottom: 28 }}>
          <Avatar initial="微" size={44} />
          <span className="c4" style={{ fontSize: 13 }}>
            →
          </span>
          <LogoMark size={40} />
        </div>
        <h1 className="lg-title">第一次用微信登录</h1>
        <p className="lg-desc">
          个人微信需要和工作区里的成员绑定一次。
          <br />
          请输入管理员登记的工作邮箱。
        </p>
      </div>
      <form
        className="lg-actions"
        onSubmit={(e) => {
          e.preventDefault();
          void (sent ? bind() : send());
        }}
      >
        <label className="lg-field">
          <span className="sr-only">工作邮箱</span>
          <input type="email" autoComplete="email" placeholder="工作邮箱" value={email} onChange={(e) => setEmail(e.target.value)} />
        </label>
        <label className="lg-field">
          <span className="sr-only">验证码</span>
          <input
            ref={codeRef}
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            placeholder="邮件验证码"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
          />
          {sent && left > 0 ? (
            <span className="side">{left} 秒</span>
          ) : (
            <button type="button" className="side" onClick={() => void send()}>
              {sent ? '重新发送' : '获取验证码'}
            </button>
          )}
        </label>
        <button type="submit" className="lg-btn primary" disabled={busy || !sent}>
          绑定并登录
        </button>
      </form>
      <ErrorLine error={error} />
      <p className={clsx('lg-note', narrow && 'lg-center')}>
        绑定后，这个微信以后扫码即可直接登录；
        <br />
        换手机或换微信号时，可在「我 › 账号」里解绑。
      </p>
    </>
  );
}

// ---------------------------------------------------------------------------
// 25d 邮箱验证码：6 位，自动跳格
// ---------------------------------------------------------------------------

function CodeStep({
  email,
  narrow,
  go,
  onDone,
  sendEmail,
  setError,
  error,
}: StepProps & { email: string; onDone: () => void; sendEmail: (email: string, onNotMember: () => void) => Promise<boolean> }) {
  const [digits, setDigits] = useState<string[]>(['', '', '', '', '', '']);
  const [sentKey, setSentKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const left = useCountdown(60, sentKey);
  const refs = useRef<(HTMLInputElement | null)[]>([]);
  useEffect(() => refs.current[0]?.focus(), []);

  const submit = async (code: string) => {
    setBusy(true);
    try {
      await post('/auth/email/login', { email, code });
      onDone();
    } catch (e) {
      setError(errorText(e));
      setDigits(['', '', '', '', '', '']);
      refs.current[0]?.focus();
    } finally {
      setBusy(false);
    }
  };

  const fill = (start: number, text: string) => {
    const chars = text.replace(/\D/g, '').split('');
    if (chars.length === 0) return;
    const next = [...digits];
    let i = start;
    for (const ch of chars) {
      if (i > 5) break;
      next[i++] = ch;
    }
    setDigits(next);
    setError(null);
    refs.current[Math.min(i, 5)]?.focus();
    if (next.every((d) => d)) void submit(next.join(''));
  };

  return (
    <>
      <Heading narrow={narrow} logo={narrow} title="查收验证码" desc={`已发送到 ${email}`} />
      <div className="lg-code" style={narrow ? undefined : { margin: '32px 0 0' }} role="group" aria-label="6 位验证码">
        {digits.map((d, i) => (
          <input
            key={i}
            ref={(el) => {
              refs.current[i] = el;
            }}
            inputMode="numeric"
            autoComplete={i === 0 ? 'one-time-code' : 'off'}
            maxLength={6}
            value={d}
            disabled={busy}
            aria-label={`第 ${i + 1} 位`}
            onChange={(e) => fill(i, e.target.value)}
            onPaste={(e) => {
              e.preventDefault();
              fill(i, e.clipboardData.getData('text'));
            }}
            onKeyDown={(e) => {
              if (e.key === 'Backspace' && !digits[i] && i > 0) {
                const next = [...digits];
                next[i - 1] = '';
                setDigits(next);
                refs.current[i - 1]?.focus();
              }
              if (e.key === 'ArrowLeft' && i > 0) refs.current[i - 1]?.focus();
              if (e.key === 'ArrowRight' && i < 5) refs.current[i + 1]?.focus();
            }}
          />
        ))}
      </div>
      <ErrorLine error={error} />
      <div className="lg-row" style={{ marginTop: 24, justifyContent: narrow ? 'center' : 'flex-start' }}>
        <span className="c4">没收到？</span>
        {left > 0 ? (
          <span className="c3">{left} 秒后可重新发送</span>
        ) : (
          <button
            type="button"
            className="c2"
            onClick={async () => {
              if (await sendEmail(email, () => go({ s: 'not-member', email }))) setSentKey((k) => k + 1);
            }}
          >
            重新发送
          </button>
        )}
      </div>
      <div className="lg-row" style={{ marginTop: 32, justifyContent: narrow ? 'center' : 'flex-start' }}>
        <button type="button" className="lg-link" onClick={() => go({ s: 'home' })}>
          <Icon name="chevL" size={14} />
          <span>换一个邮箱</span>
        </button>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------

function InfoModal({ kind, onClose }: { kind: 'data' | 'privacy' | 'admin'; onClose: () => void }) {
  const content = {
    data: {
      title: '数据说明',
      body: (
        <>
          <p>草稿和提交前的修改只有你自己能看到。提交后，直属主管能看到你的日报、AI 追问和你的回复。</p>
          <p style={{ marginTop: 10 }}>“建议关注 / 值得认可”只有直属主管能看到；周报只谈项目，不点评个人。</p>
        </>
      ),
    },
    privacy: {
      title: '隐私',
      body: (
        <>
          <p>日报数据存放在公司的国内服务器上。AI 处理时，人名和客户名会先替换成代号；AI 只提示，不替人做决定。</p>
          <p style={{ marginTop: 10 }}>离职交接完成后，与你相关的“建议关注”记录会被删除，历史日报保留、只读。</p>
        </>
      ),
    },
    admin: {
      title: '联系管理员',
      body: <p>登录不了或者不在工作区里，请联系你的直属主管或 IT 管理员，把你的工作邮箱加入日报工作区。</p>,
    },
  }[kind];
  return (
    <Modal width={440} title={content.title} onClose={onClose}>
      <div className="t-body">{content.body}</div>
    </Modal>
  );
}
